import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useI18n } from "../i18n";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";
import { money, waLink } from "../lib/site";

export default function CartDrawer({ settings }) {
  const { lang, t } = useI18n();
  const { items, setQty, remove, subtotal, open, setOpen, clear } = useCart();
  const navigate = useNavigate();
  const [checkout, setCheckout] = useState(false);
  const [form, setForm] = useState({ customer_name: "", phone: "", order_type: "pickup", notes: "", address: "", location: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(null);
  const submitRef = useRef(null);

  useEffect(() => { api.get("/discount").then((r) => setDiscount(r.data)).catch(() => {}); }, []);

  const currency = settings?.currency || "C$";
  const taxPercent = settings?.tax_percent ?? 15;
  const discountPercent = discount && discount.active && Number(discount.percent) > 0 ? Number(discount.percent) : 0;
  const discountAmount = subtotal * discountPercent / 100;
  const discountedSubtotal = subtotal - discountAmount;
  const tax = discountedSubtotal * taxPercent / 100;
  const deliveryFee = form.order_type === "delivery" ? Number(settings?.delivery_fee || 0) : 0;
  const total = discountedSubtotal + tax + deliveryFee;
  const discountLabel = discount ? (lang === "es" ? discount.label_es : discount.label_en) || (lang === "es" ? "Descuento" : "Discount") : "";

  const capturePin = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const { latitude, longitude } = pos.coords;
      setForm((f) => ({ ...f, location: `https://maps.google.com/?q=${latitude},${longitude}` }));
    });
  };

  const submit = async () => {
    setError("");
    if (!form.customer_name.trim() || form.phone.trim().length < 6) { setError(t.cart.fillRequired); return; }
    setLoading(true);
    // Open the WhatsApp tab synchronously within the user gesture so mobile popup blockers don't stop it.
    const waWindow = window.open("", "_blank");
    try {
      const { data } = await api.post("/orders", { ...form, items });
      const cur = data.currency || currency;
      const lines = data.items.map((i) => `• ${i.qty}x ${i.name_es} — ${money(i.price * i.qty, cur)}`).join("\n");
      const typeLabel = data.order_type === "delivery" ? "🛵 Entrega a domicilio" : "🥡 Para llevar";
      const locBlock = data.order_type === "delivery" ? `\n📍 ${data.address || "-"}${data.location ? `\n🗺️ ${data.location}` : ""}` : "";
      const waText = `🍽️ *NUEVO PEDIDO — El Timón San Juan del Sur*\n*${data.order_number}*\n\n👤 ${data.customer_name}\n📞 ${data.phone}\n${typeLabel}${locBlock}\n\n*Pedido:*\n${lines}\n\nSubtotal: ${money(data.subtotal, cur)}${data.discount_amount > 0 ? `\nDescuento (${data.discount_percent}%): -${money(data.discount_amount, cur)}` : ""}\nImpuesto (${data.tax_percent}%): ${money(data.tax, cur)}${data.delivery_fee > 0 ? `\nEnvío: ${money(data.delivery_fee, cur)}` : ""}\n*TOTAL: ${money(data.total, cur)}*${data.notes ? `\n\n📝 ${data.notes}` : ""}`;
      if (waWindow) waWindow.location.href = waLink(waText);
      clear();
      setOpen(false);
      setCheckout(false);
      navigate("/order-confirmation", { state: { order: data } });
    } catch (e) {
      if (waWindow) waWindow.close();
      setError(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/60 z-[60]" onClick={() => setOpen(false)} data-testid="cart-overlay" />}
      <div data-testid="cart-drawer" className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-[#0A192F] border-l border-amber-500/20 z-[70] transform transition-transform duration-300 flex flex-col ${open ? "translate-x-0" : "translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="font-display text-xl text-amber-400">{t.cart.title}</h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close-btn" className="p-2 text-slate-300 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-500 gap-3 px-6 text-center">
            <ShoppingBag className="h-12 w-12 opacity-40" />
            <p>{t.cart.empty}</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex items-center gap-3 bg-[#112240] rounded-xl p-3">
                  <div className="flex-1">
                    <div className="text-white text-sm font-medium">{lang === "es" ? i.name_es : i.name_en}</div>
                    <div className="text-amber-400 text-sm">{money(i.price, currency)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-dec-${i.id}`} className="h-7 w-7 rounded-full bg-[#1D2D50] text-white flex items-center justify-center hover:bg-amber-500 hover:text-slate-950"><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-6 text-center text-white text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                    <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-inc-${i.id}`} className="h-7 w-7 rounded-full bg-[#1D2D50] text-white flex items-center justify-center hover:bg-amber-500 hover:text-slate-950"><Plus className="h-3.5 w-3.5" /></button>
                    <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.id}`} className="ml-1 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 px-5 py-4 space-y-2 max-h-[62vh] overflow-y-auto">
              <div className="flex justify-between text-slate-300 text-sm"><span>{t.cart.subtotal}</span><span data-testid="cart-subtotal">{money(subtotal, currency)}</span></div>
              {discountPercent > 0 && <div className="flex justify-between text-green-400 text-sm"><span>{discountLabel} ({discountPercent}%)</span><span data-testid="cart-discount">−{money(discountAmount, currency)}</span></div>}
              <div className="flex justify-between text-slate-300 text-sm"><span>{t.cart.tax} ({taxPercent}%)</span><span data-testid="cart-tax">{money(tax, currency)}</span></div>
              {deliveryFee > 0 && <div className="flex justify-between text-slate-300 text-sm"><span>{t.cart.deliveryFee}</span><span data-testid="cart-delivery-fee">{money(deliveryFee, currency)}</span></div>}
              <div className="flex justify-between text-white font-semibold text-lg"><span>{t.cart.total}</span><span data-testid="cart-total">{money(total, currency)}</span></div>

              {!checkout ? (
                <button onClick={() => setCheckout(true)} data-testid="cart-checkout-btn" className="w-full mt-3 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-3 rounded-full transition-all">{t.cart.checkout}</button>
              ) : (
                <div className="mt-3 space-y-3" data-testid="checkout-form">
                  <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder={t.cart.name} data-testid="checkout-name-input" className="w-full bg-[#112240] border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 outline-none" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t.cart.phone} data-testid="checkout-phone-input" className="w-full bg-[#112240] border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 outline-none" />
                  <div className="flex gap-2">
                    {["pickup", "delivery"].map((ot) => (
                      <button key={ot} onClick={() => { setForm({ ...form, order_type: ot }); if (ot === "delivery") setTimeout(() => submitRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150); }} data-testid={`checkout-type-${ot}`}
                        className={`flex-1 text-xs py-2 rounded-lg border transition-all ${form.order_type === ot ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-slate-700 text-slate-400"}`}>
                        {ot === "delivery" ? t.cart.delivery : t.cart.pickup}
                      </button>
                    ))}
                  </div>
                  {form.order_type === "delivery" && (
                    <div className="space-y-2" data-testid="delivery-fields">
                      <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t.cart.address} rows={2} data-testid="checkout-address-input" className="w-full bg-[#112240] border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 outline-none resize-none" />
                      <button type="button" onClick={capturePin} data-testid="checkout-pin-btn" className={`w-full inline-flex items-center justify-center gap-2 text-xs rounded-lg py-2 border transition-all ${form.location ? "border-teal-500 bg-teal-500/10 text-teal-300" : "border-teal-500/40 text-teal-300 hover:bg-teal-500/10"}`}>
                        <i className="fa-solid fa-location-dot" /> {form.location ? t.cart.locationCaptured : t.cart.useLocation}
                      </button>
                    </div>
                  )}
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={t.cart.notes} rows={2} data-testid="checkout-notes-input" className="w-full bg-[#112240] border border-slate-700 rounded-lg px-3 py-2.5 text-white text-sm focus:border-amber-500 outline-none resize-none" />
                  <p className="text-[11px] text-slate-500 leading-snug">{t.cart.hint}</p>
                  {error && <p className="text-red-400 text-xs" data-testid="checkout-error">{error}</p>}
                  <button ref={submitRef} onClick={submit} disabled={loading} data-testid="place-order-btn" className="w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-slate-950 font-semibold py-3 rounded-full transition-all disabled:opacity-50"><i className="fa-brands fa-whatsapp text-lg" /> {loading ? "..." : t.cart.placeOrder}</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
